<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, toRaw, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useDebounceFn } from "@vueuse/core"
import { useToast } from "primevue/usetoast"
import type { BuildingBboxItem } from "@core/services/bati.service"
import { coreServiceRegistry } from "@core/services/register"
import { ServiceError } from "@core/exceptions"
import type { Services } from "@services"
import type { EnedisUndergroundLineRecord } from "@core/services/enedis.service"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            skipLabel: "Je ne trouve pas mon bâtiment sur la carte",
            fetchError: "Impossible de charger les bâtiments sur la carte",
            zoomToSeeParcels: "Zoomez pour afficher les parcelles",
            addressMarkerTooltip: "Adresse",
            editParcel: "Éditer la parcelle sur la carte",
            cancelParcelEdit:
                "Annuler l’édition et restaurer la parcelle initiale",
            finishParcelEdit: "Terminer l’édition (conserver la sélection)",
            undergroundBtToggleShow: "Afficher les lignes souterraines BT",
            undergroundBtToggleHide: "Masquer les lignes souterraines BT",
            undergroundHtaToggleShow: "Afficher les lignes souterraines HTA",
            undergroundHtaToggleHide: "Masquer les lignes souterraines HTA",
            legendBt: "Lignes souterraines BT",
            legendHta: "Lignes souterraines HTA",
            legendBtTooltip:
                "Il s’agit des lignes du réseau électrique enterré les plus proches des bâtiments — celles qui desservent l’usage courant. Cliquez pour afficher ou masquer ces tracés sur la carte.",
            legendHtaTooltip:
                "Il s’agit des lignes enterrées qui alimentent le secteur en amont, avant la distribution vers les rues et les immeubles. Cliquez pour afficher ou masquer ces tracés sur la carte.",
            undergroundFetchError:
                "Impossible de charger les lignes souterraines Enedis",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Géométrie parcelle (GeoJSON Geometry)
 */
export interface ParcelGeometry {
    type: string
    coordinates?: unknown
}

export interface ModelParcel {
    parcelId: string
    parcelGeometry: ParcelGeometry | null
}

/** Méthodes exposées au parent (template ref) */
export type BuildingFormParcelMapExpose = {
    exitParcelEditMode: () => void
}

/**
 * Certaines APIs renvoient `type: MultiPolygon` avec la même imbrication qu’un Polygon
 * (anneaux directement sous `coordinates`), ce qui est invalide en GeoJSON et Leaflet
 * ne dessine pas la forme. On corrige en `Polygon` lorsque c’est manifestement le cas.
 */
function coerceMislabeledMultiPolygonToPolygon(
    g: ParcelGeometry & { type: "Polygon" | "MultiPolygon" }
): ParcelGeometry & { type: "Polygon" | "MultiPolygon" } {
    if (g.type !== "MultiPolygon" || !Array.isArray(g.coordinates)) {
        return g
    }
    const c = g.coordinates as unknown[]
    const outer = c[0]
    const firstInRing = Array.isArray(outer) ? outer[0] : undefined
    if (
        Array.isArray(firstInRing) &&
        typeof firstInRing[0] === "number" &&
        typeof firstInRing[1] === "number" &&
        !Array.isArray(firstInRing[0])
    ) {
        return {
            type: "Polygon",
            coordinates: g.coordinates,
        } as ParcelGeometry & { type: "Polygon" | "MultiPolygon" }
    }
    return g
}

/**
 * Accepte une géométrie Polygon/MultiPolygon ou une Feature GeoJSON enveloppante.
 */
function normalizeParcelGeometry(
    raw: unknown
): (ParcelGeometry & { type: "Polygon" | "MultiPolygon" }) | null {
    if (!raw || typeof raw !== "object") return null
    const o = raw as Record<string, unknown>
    if (o.type === "Feature" && o.geometry && typeof o.geometry === "object") {
        const g = o.geometry as ParcelGeometry
        if (g.type === "Polygon" || g.type === "MultiPolygon") {
            return coerceMislabeledMultiPolygonToPolygon(
                g as ParcelGeometry & { type: "Polygon" | "MultiPolygon" }
            )
        }
        return null
    }
    if (o.type === "Polygon" || o.type === "MultiPolygon") {
        return coerceMislabeledMultiPolygonToPolygon(
            o as unknown as ParcelGeometry & {
                type: "Polygon" | "MultiPolygon"
            }
        )
    }
    return null
}

/**
 * Centre approximatif [lng, lat] (premier anneau) pour positionner la carte sans point d’adresse.
 */
function getCentroidLngLat(
    g: ParcelGeometry & { type: "Polygon" | "MultiPolygon" }
): [number, number] | null {
    const coords = g.coordinates as number[][][] | number[][][][] | undefined
    let ring: [number, number][] | null = null
    if (g.type === "Polygon" && Array.isArray(coords?.[0])) {
        ring = coords[0] as [number, number][]
    } else if (g.type === "MultiPolygon" && Array.isArray(coords?.[0]?.[0])) {
        ring = coords[0][0] as [number, number][]
    }
    if (!ring?.length) return null
    let sumLng = 0
    let sumLat = 0
    let n = 0
    for (const pt of ring) {
        if (Array.isArray(pt) && pt.length >= 2) {
            sumLng += pt[0]
            sumLat += pt[1]
            n++
        }
    }
    if (n === 0) return null
    return [sumLng / n, sumLat / n]
}

/**
 * Copie profonde de la géométrie pour sauvegarder l’état initial avant édition.
 */
function cloneParcelGeometry(g: ParcelGeometry | null): ParcelGeometry | null {
    if (!g) return null
    const raw = toRaw(g) as ParcelGeometry
    try {
        return structuredClone(raw)
    } catch {
        return JSON.parse(JSON.stringify(raw)) as ParcelGeometry
    }
}

/**
 * Instantané de la parcelle courante (props) pour restauration à l’annulation.
 */
function snapshotModelParcelFromProps(m: ModelParcel | undefined): ModelParcel {
    const src = m ?? { parcelId: "", parcelGeometry: null }
    return {
        parcelId: src.parcelId,
        parcelGeometry: cloneParcelGeometry(src.parcelGeometry),
    }
}

export interface Props {
    /**
     * Adresse formatée (texte) pour affichage / contexte
     */
    addressInline: string
    /**
     * Coordonnées de l'adresse [longitude, latitude] (GeoJSON)
     */
    addressLocation: [number, number] | null
    /**
     * Parcelle sélectionnée (v-model)
     */
    modelParcel?: ModelParcel
    /**
     * Utilisateur ne souhaite pas sélectionner de parcelle (v-model)
     */
    modelSkipParcel?: boolean
    /**
     * Lecture seule : affiche uniquement la carte et la parcelle sélectionnée si présente
     */
    readonly?: boolean
    /**
     * Masquer le bouton "Je ne trouve pas mon bâtiment"
     */
    disabledSkipButton?: boolean
    /**
     * Réservé à une future couche « réseau Enedis » ; n’affecte pas les lignes
     * souterraines BT/HTA (open data), contrôlées par `showUndergroundBtHtaLines`.
     */
    disableEnedisNetwork?: boolean
    /**
     * Si true : un bâtiment a déjà un cadastre — la carte est en « vue » jusqu’au clic sur le crayon.
     * Si false : pas de cadastre (ou parcours création / changement d’adresse) — recherche des parcelles dès l’affichage.
     */
    parcelSearchRequiresEditClick?: boolean
    /**
     * Lignes souterraines Enedis BT + HTA (open data) : chargement, légende et tracés hors édition de parcelle sur carte.
     * @see https://opendata.enedis.fr/data-fair/api/v1/datasets/reseau-souterrain-bt
     * @see https://opendata.enedis.fr/data-fair/api/v1/datasets/reseau-souterrain-hta
     */
    showUndergroundBtHtaLines?: boolean
}

interface Emits {
    (e: "update:modelParcel", value: ModelParcel): void
    (e: "update:modelSkipParcel", value: boolean): void
    (e: "state:parcelLoad"): void
    (e: "state:parcelLoaded"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelParcel: () => ({ parcelId: "", parcelGeometry: null }),
    modelSkipParcel: false,
    readonly: false,
    disabledSkipButton: false,
    disableEnedisNetwork: true,
    parcelSearchRequiresEditClick: false,
    showUndergroundBtHtaLines: false,
})

const emit = defineEmits<Emits>()

// ================================
// SERVICES
// ================================
const services = inject<Services | undefined>("services", undefined)

/**
 * Accès au service Bati : `$getBuildingsLocalisations` injecté (app) ou registry (tests / Storybook sans inject).
 */
function getBati(): Pick<
    typeof coreServiceRegistry.bati,
    "getBuildingsLocalisations"
> {
    if (services?.$getBuildingsLocalisations) {
        return {
            getBuildingsLocalisations: (bounds) =>
                services.$getBuildingsLocalisations(bounds),
        }
    }
    return coreServiceRegistry.bati
}

/**
 * Service Enedis (open data lignes souterraines).
 */
function getEnedisService(): Pick<
    typeof coreServiceRegistry.enedis,
    "searchUndergroundNetwork"
> {
    return coreServiceRegistry.enedis
}

// ================================
// ÉTAT
// ================================
const toast = useToast()
const buildings = ref<BuildingBboxItem[]>([])
const fetchPending = ref(false)
let fetchSeq = 0
/** Signature des bounds déjà chargées avec succès (évite la boucle geojson → BaseMap → update:bounds) */
const lastSuccessfulBoundsSignature = ref<string | null>(null)
/** Signature du fetch en cours (évite deux requêtes identiques en parallèle) */
const inFlightBoundsSignature = ref<string | null>(null)
/** Signature des bounds ayant retourné "trop-de-batiments-dans-bbox" */
const tooManyBuildingsBoundsSignature = ref<string | null>(null)
/** Affiche un message d'aide invitant à zoomer */
const showZoomHint = ref(false)
let undergroundFetchSeq = 0
/**
 * Si true : chargement des parcelles / bâtiments (API Bati) et sélection sur la carte.
 * Sinon : affichage de la parcelle déjà enregistrée sans appels réseau.
 */
const parcelEditActive = ref(false)
/** Parcelle + case « ignorée » au moment où l’édition est lancée (annulation = restauration). */
const baselineParcel = ref<ModelParcel | null>(null)
const baselineSkipParcel = ref(false)
/**
 * Style Leaflet transmis dans le GeoJSON (`properties.pathStyle`) pour l’affichage carte.
 */
const UNDERGROUND_LINE_PATH_STYLE_BT = {
    color: "#e34a4a",
    weight: 2,
    opacity: 0.95,
} as const

const UNDERGROUND_LINE_PATH_STYLE_HTA = {
    color: "#2563eb",
    weight: 2,
    opacity: 0.95,
} as const

/** Segments GeoJSON lignes souterraines (BT / HTA) — chargés via API Enedis open data */
const undergroundGeoFeatures = ref<
    Array<{
        type: "Feature"
        properties: {
            lineKind: "enedis-bt" | "enedis-hta"
            pathStyle: {
                color: string
                weight: number
                opacity: number
            }
        }
        geometry: EnedisUndergroundLineRecord["geometry"]
    }>
>([])
/**
 * Affichage des lignes BT / HTA sur la carte (bascules indépendantes sous la carte).
 * Par défaut à true : dès que les données sont chargées, elles sont fusionnées dans le GeoJSON.
 */
const showUndergroundBtOnMap = ref(true)
const showUndergroundHtaOnMap = ref(true)

// ================================
// COMPUTED
// ================================
/**
 * Centre de la carte [latitude, longitude] pour BaseMap (Leaflet).
 * Priorité : point d’adresse (GeoJSON [lng, lat]), sinon barycentre de la parcelle en lecture seule.
 */
const getCenter = computed<[number, number] | null>(() => {
    if (props.addressLocation && props.addressLocation.length >= 2) {
        const [lng, lat] = props.addressLocation
        return [lat, lng]
    }
    if (props.readonly) {
        const normalized = normalizeParcelGeometry(
            props.modelParcel?.parcelGeometry
        )
        if (normalized) {
            const ll = getCentroidLngLat(normalized)
            if (ll) {
                const [lng, lat] = ll
                return [lat, lng]
            }
        }
    }
    return null
})

/** Skeleton si aucune coordonnée pour centrer (adresse ou parcelle en readonly) */
const getLoading = computed(() => getCenter.value === null)

/**
 * Lignes souterraines à tracer selon les bascules BT / HTA.
 */
function getVisibleUndergroundFeatures(
    undergroundLines: typeof undergroundGeoFeatures.value
): typeof undergroundGeoFeatures.value {
    return undergroundLines.filter((f) => {
        if (f.properties.lineKind === "enedis-bt") {
            return showUndergroundBtOnMap.value
        }
        if (f.properties.lineKind === "enedis-hta") {
            return showUndergroundHtaOnMap.value
        }
        return false
    })
}

/**
 * Ajoute les lignes souterraines au jeu de features lorsque la couche est activée.
 * @param undergroundLines — snapshot passé depuis le computed pour lier la réactivité à `undergroundGeoFeatures`.
 */
function mergeUndergroundFeatures(
    features: Array<Record<string, unknown>>,
    undergroundLines: typeof undergroundGeoFeatures.value
): Array<Record<string, unknown>> {
    if (!props.showUndergroundBtHtaLines || parcelEditActive.value) {
        return features
    }
    const visible = getVisibleUndergroundFeatures(undergroundLines)
    if (visible.length === 0) {
        return features
    }
    return [...features, ...visible]
}

/**
 * GeoJSON affiché : bâtiments API ou parcelle + marqueur adresse en lecture seule
 */
const getMapGeoJson = computed(() => {
    const undergroundLines = undergroundGeoFeatures.value

    if (props.readonly) {
        const normalized = normalizeParcelGeometry(
            props.modelParcel?.parcelGeometry
        )
        const hasAddress =
            props.addressLocation && props.addressLocation.length >= 2
        const features: Array<Record<string, unknown>> = []

        if (normalized) {
            features.push({
                type: "Feature" as const,
                properties: {
                    id_building: props.modelParcel?.parcelId || "parcel",
                    parcelStyle: "selected" as const,
                },
                geometry: {
                    type: normalized.type,
                    coordinates: normalized.coordinates,
                },
            })
        }

        if (hasAddress) {
            const [lng, lat] = props.addressLocation!
            const label =
                props.addressInline?.trim() || t("addressMarkerTooltip")
            features.push({
                type: "Feature" as const,
                properties: {
                    name: label,
                    title: label,
                },
                geometry: {
                    type: "Point" as const,
                    coordinates: [lng, lat] as [number, number],
                },
            })
        }

        if (features.length > 0) {
            return {
                type: "FeatureCollection" as const,
                features: mergeUndergroundFeatures(features, undergroundLines),
            }
        }
        /** Parcelle / point absents mais lignes souterraines chargées (carte centrée sur adresse ailleurs) */
        if (props.showUndergroundBtHtaLines && undergroundLines.length > 0) {
            const merged = mergeUndergroundFeatures([], undergroundLines)
            if (merged.length > 0) {
                return {
                    type: "FeatureCollection" as const,
                    features: merged,
                }
            }
        }
    }

    /**
     * Mode édition : polygones issus de l’API Bati dans la bbox courante.
     * La parcelle du modèle (ex. id BDNB) peut être absente de cette liste :
     * on l’ajoute pour que BaseMap affiche la sélection après clic ou rechargement.
     */
    const fromBuildings = buildings.value.map((b) => ({
        type: "Feature" as const,
        properties: {
            id_building: b.id_building,
            parcelStyle:
                props.modelParcel?.parcelId === b.id_building
                    ? ("selected" as const)
                    : ("default" as const),
        },
        geometry: b.geometry,
    }))

    const normalizedEdit = normalizeParcelGeometry(
        props.modelParcel?.parcelGeometry
    )
    const selectedParcelId = props.modelParcel?.parcelId?.trim()
    const selectedInBuildings =
        !!selectedParcelId &&
        buildings.value.some((b) => b.id_building === selectedParcelId)

    const featuresEdit: Array<Record<string, unknown>> = [...fromBuildings]

    if (normalizedEdit && selectedParcelId && !selectedInBuildings) {
        featuresEdit.push({
            type: "Feature" as const,
            properties: {
                id_building: selectedParcelId,
                parcelStyle: "selected" as const,
            },
            geometry: {
                type: normalizedEdit.type,
                coordinates: normalizedEdit.coordinates,
            },
        })
    }

    /** Marqueur d’adresse (même rendu qu’en readonly) : visible dès que le point est connu. */
    if (props.addressLocation && props.addressLocation.length >= 2) {
        const [lng, lat] = props.addressLocation
        const label = props.addressInline?.trim() || t("addressMarkerTooltip")
        featuresEdit.push({
            type: "Feature" as const,
            properties: {
                name: label,
                title: label,
            },
            geometry: {
                type: "Point" as const,
                coordinates: [lng, lat] as [number, number],
            },
        })
    }

    return {
        type: "FeatureCollection" as const,
        features: mergeUndergroundFeatures(featuresEdit, undergroundLines),
    }
})

// ================================
// CHARGEMENT BÂTIMENTS (API Bati)
// ================================
/**
 * Convertit les enregistrements open data en features GeoJSON (lignes).
 */
function recordsToUndergroundFeatures(
    records: EnedisUndergroundLineRecord[],
    lineKind: "enedis-bt" | "enedis-hta"
): typeof undergroundGeoFeatures.value {
    return records
        .filter(
            (r) =>
                r.geometry?.type === "LineString" ||
                r.geometry?.type === "MultiLineString"
        )
        .map((r) => ({
            type: "Feature" as const,
            properties: {
                lineKind,
                pathStyle:
                    lineKind === "enedis-bt"
                        ? { ...UNDERGROUND_LINE_PATH_STYLE_BT }
                        : { ...UNDERGROUND_LINE_PATH_STYLE_HTA },
            },
            geometry: r.geometry,
        }))
}

/**
 * Fusionne les réponses BT / HTA en features carte.
 */
function mergeUndergroundSettledResults(
    btSettled: PromiseSettledResult<EnedisUndergroundLineRecord[]>,
    htaSettled: PromiseSettledResult<EnedisUndergroundLineRecord[]>
): typeof undergroundGeoFeatures.value {
    const next: typeof undergroundGeoFeatures.value = []
    if (btSettled.status === "fulfilled") {
        next.push(...recordsToUndergroundFeatures(btSettled.value, "enedis-bt"))
    }
    if (htaSettled.status === "fulfilled") {
        next.push(
            ...recordsToUndergroundFeatures(htaSettled.value, "enedis-hta")
        )
    }
    return next
}

/**
 * Lignes souterraines autour du point d’adresse (lecture seule ou hors édition parcelle sur carte).
 */
async function fetchUndergroundLinesFromAddress(): Promise<void> {
    if (
        !props.showUndergroundBtHtaLines ||
        !props.addressLocation ||
        props.addressLocation.length < 2 ||
        parcelEditActive.value
    ) {
        undergroundGeoFeatures.value = []
        return
    }
    const seq = ++undergroundFetchSeq
    const [lng, lat] = props.addressLocation
    const enedis = getEnedisService()
    const [btSettled, htaSettled] = await Promise.allSettled([
        enedis.searchUndergroundNetwork({
            localisation: [lat, lng],
            dataset: "reseau-souterrain-bt",
        }),
        enedis.searchUndergroundNetwork({
            localisation: [lat, lng],
            dataset: "reseau-souterrain-hta",
        }),
    ])
    if (seq !== undergroundFetchSeq) return
    undergroundGeoFeatures.value = mergeUndergroundSettledResults(
        btSettled,
        htaSettled
    )
    if (btSettled.status === "rejected" && htaSettled.status === "rejected") {
        toast.add({
            severity: "error",
            summary: t("undergroundFetchError"),
            life: 5000,
        })
    }
}

/**
 * Bounding box autour d’un point [lng, lat] pour premier chargement avant moveend
 */
function bboxAroundAddress(
    lng: number,
    lat: number,
    delta = 0.003
): {
    lat_min: number
    lat_max: number
    lon_min: number
    lon_max: number
} {
    return {
        lat_min: lat - delta,
        lat_max: lat + delta,
        lon_min: lng - delta,
        lon_max: lng + delta,
    }
}

/**
 * Clé stable des bounds (évite les re-fetch dus aux flottants ou au même viewport)
 */
function getBoundsSignature(b: {
    lat_min: number
    lat_max: number
    lon_min: number
    lon_max: number
}): string {
    const q = (n: number) => n.toFixed(5)
    return `${q(b.lat_min)}|${q(b.lat_max)}|${q(b.lon_min)}|${q(b.lon_max)}`
}

async function fetchBuildingsInBounds(b: {
    lat_min: number
    lat_max: number
    lon_min: number
    lon_max: number
}): Promise<void> {
    if (props.readonly) return
    if (!parcelEditActive.value) return

    const signature = getBoundsSignature(b)
    if (signature === lastSuccessfulBoundsSignature.value) return
    if (signature === tooManyBuildingsBoundsSignature.value) {
        showZoomHint.value = true
        return
    }
    if (fetchPending.value && inFlightBoundsSignature.value === signature) {
        return
    }

    const seq = ++fetchSeq
    inFlightBoundsSignature.value = signature
    fetchPending.value = true
    emit("state:parcelLoad")

    try {
        const list = await getBati().getBuildingsLocalisations(b)
        if (seq !== fetchSeq) return
        buildings.value = list
        lastSuccessfulBoundsSignature.value = signature
        tooManyBuildingsBoundsSignature.value = null
        showZoomHint.value = false
        emit("state:parcelLoaded")
    } catch (err) {
        if (seq !== fetchSeq) return
        if (
            err instanceof ServiceError &&
            err.code === "trop-de-batiments-dans-bbox"
        ) {
            tooManyBuildingsBoundsSignature.value = signature
            showZoomHint.value = true
            buildings.value = []
            return
        }

        buildings.value = []
        const message =
            err instanceof ServiceError ? err.message : t("fetchError")
        toast.add({
            severity: "error",
            summary: message,
            life: 5000,
        })
    } finally {
        if (seq === fetchSeq) {
            fetchPending.value = false
            inFlightBoundsSignature.value = null
        }
    }
}

/** Debounce après fin de déplacement sur la carte (après émission debounced des bounds par BaseMap) */
const MAP_MOVE_DEBOUNCE_MS = 180
const debouncedFetchBuildingsFromMap = useDebounceFn(
    fetchBuildingsInBounds,
    MAP_MOVE_DEBOUNCE_MS
)
/** Debounce lors du changement d’adresse (évite rafales si le parent met à jour souvent) */
const ADDRESS_DEBOUNCE_MS = 220
const debouncedFetchBuildingsFromAddress = useDebounceFn(
    fetchBuildingsInBounds,
    ADDRESS_DEBOUNCE_MS
)

// ================================
// HANDLERS
// ================================
/**
 * Bounds carte (debounced côté BaseMap) → recharge les localisations
 */
function onUpdateBounds(b: {
    lat_min: number
    lat_max: number
    lon_min: number
    lon_max: number
}): void {
    if (!props.readonly && parcelEditActive.value) {
        debouncedFetchBuildingsFromMap(b)
    }
}

/**
 * Sélection d’un bâtiment sur la carte
 */
function onSelectPolygon(feature: {
    properties?: { id_building?: string }
    geometry: { type: string; coordinates?: unknown }
}): void {
    if (!parcelEditActive.value) return
    const id = feature.properties?.id_building
    if (!id) return
    emit("update:modelSkipParcel", false)
    emit("update:modelParcel", {
        parcelId: id,
        parcelGeometry: {
            type: feature.geometry.type,
            coordinates: feature.geometry.coordinates,
        },
    })
}

/**
 * Coche / décoche "Je ne trouve pas mon bâtiment"
 */
function onUpdateSkipParcel(value: boolean): void {
    emit("update:modelSkipParcel", value)
    if (value) {
        emit("update:modelParcel", { parcelId: "", parcelGeometry: null })
    }
}

/**
 * Démarre l’édition parcelle : mémorise l’état actuel pour pouvoir annuler.
 */
function onStartParcelEdit(): void {
    baselineParcel.value = snapshotModelParcelFromProps(props.modelParcel)
    baselineSkipParcel.value = props.modelSkipParcel
    parcelEditActive.value = true
}

/**
 * Annule l’édition et restaure la parcelle / case « ignorée » tels qu’au début.
 */
function onCancelParcelEdit(): void {
    if (baselineParcel.value) {
        emit("update:modelParcel", baselineParcel.value)
        emit("update:modelSkipParcel", baselineSkipParcel.value)
    }
    baselineParcel.value = null
    parcelEditActive.value = props.parcelSearchRequiresEditClick ? false : true
}

/**
 * Affiche ou masque uniquement les lignes BT (clic sur l’entrée de légende BT).
 */
function onToggleUndergroundBtOnMap(): void {
    showUndergroundBtOnMap.value = !showUndergroundBtOnMap.value
}

/**
 * Affiche ou masque uniquement les lignes HTA (clic sur l’entrée de légende HTA).
 */
function onToggleUndergroundHtaOnMap(): void {
    showUndergroundHtaOnMap.value = !showUndergroundHtaOnMap.value
}

/**
 * Quitte le mode édition en conservant la parcelle courante (ex. après validation API côté parent).
 */
function exitParcelEditMode(): void {
    baselineParcel.value = null
    parcelEditActive.value = !props.parcelSearchRequiresEditClick
}

defineExpose({
    exitParcelEditMode,
})

// ================================
// WATCHERS
// ================================
watch(
    () => props.addressLocation,
    (loc) => {
        if (props.readonly) return
        buildings.value = []
        lastSuccessfulBoundsSignature.value = null
        inFlightBoundsSignature.value = null
        tooManyBuildingsBoundsSignature.value = null
        showZoomHint.value = false
        if (parcelEditActive.value && loc && loc.length >= 2) {
            const [lng, lat] = loc
            debouncedFetchBuildingsFromAddress(bboxAroundAddress(lng, lat))
        }
    },
    { immediate: true }
)

watch(parcelEditActive, (active) => {
    if (props.readonly) return
    if (active) {
        lastSuccessfulBoundsSignature.value = null
        tooManyBuildingsBoundsSignature.value = null
        showZoomHint.value = false
        undergroundGeoFeatures.value = []
        undergroundFetchSeq += 1
        const loc = props.addressLocation
        if (loc && loc.length >= 2) {
            const [lng, lat] = loc
            debouncedFetchBuildingsFromAddress(bboxAroundAddress(lng, lat))
        }
    } else {
        buildings.value = []
        lastSuccessfulBoundsSignature.value = null
        inFlightBoundsSignature.value = null
        tooManyBuildingsBoundsSignature.value = null
        undergroundFetchSeq += 1
        showZoomHint.value = false
        if (
            props.showUndergroundBtHtaLines &&
            props.addressLocation &&
            props.addressLocation.length >= 2
        ) {
            void fetchUndergroundLinesFromAddress()
        } else {
            undergroundGeoFeatures.value = []
        }
    }
})

watch(
    () => props.readonly,
    (ro) => {
        if (ro) {
            baselineParcel.value = null
        }
    }
)

/**
 * Vue « lecture » + crayon si cadastre déjà présent ; sinon recherche parcelle immédiate.
 */
watch(
    () =>
        [props.parcelSearchRequiresEditClick, props.readonly] as [
            boolean,
            boolean,
        ],
    ([requiresEditClick, ro]) => {
        if (ro) {
            parcelEditActive.value = false
            return
        }
        parcelEditActive.value = !requiresEditClick
    },
    { immediate: true }
)

watch(
    () =>
        [
            props.addressLocation,
            props.showUndergroundBtHtaLines,
            props.readonly,
            parcelEditActive,
        ] as const,
    () => {
        if (
            !props.showUndergroundBtHtaLines ||
            !props.addressLocation ||
            props.addressLocation.length < 2
        ) {
            undergroundGeoFeatures.value = []
            undergroundFetchSeq += 1
            return
        }
        if (parcelEditActive.value) {
            undergroundGeoFeatures.value = []
            undergroundFetchSeq += 1
            return
        }
        void fetchUndergroundLinesFromAddress()
    },
    { immediate: true }
)
</script>

<template>
    <div class="building-parcel-map relative w-full">
        <BaseSkeleton
            v-if="getLoading"
            class="h-[31.25rem] w-full rounded-lg"
        />
        <template v-else>
            <div v-if="getCenter" class="relative w-full">
                <BaseMap
                    id="building-parcel-map"
                    class="building-parcel-map__map w-full h-[31.25rem] rounded-lg shadow-sm"
                    :geojson="getMapGeoJson"
                    :center="getCenter"
                    :zoom="19"
                    :max-zoom="22"
                    :loading="parcelEditActive && fetchPending"
                    loading-message="Recherches des parcelles en cours..."
                    :silent="true"
                    :clustering="false"
                    :auto-fit-bounds="false"
                    :bounds-emit-debounce-ms="220"
                    height="31.25rem"
                    @update:bounds="onUpdateBounds"
                    @select:polygon="onSelectPolygon"
                >
                    <template #overlay>
                        <div class="flex flex-col items-end gap-2">
                            <div
                                v-if="!readonly"
                                class="flex flex-row items-center gap-2"
                            >
                                <template
                                    v-if="
                                        parcelSearchRequiresEditClick &&
                                        !parcelEditActive
                                    "
                                >
                                    <BaseButton
                                        type="button"
                                        icon="pi pi-pencil"
                                        rounded
                                        severity="secondary"
                                        class="!h-10 !w-10 shrink-0 border border-neutral-200 bg-white/95 shadow-sm"
                                        :aria-label="t('editParcel')"
                                        v-tooltip.left="t('editParcel')"
                                        data-cy="building-parcel-map-edit"
                                        @click="onStartParcelEdit"
                                    />
                                </template>
                                <template
                                    v-else-if="
                                        parcelSearchRequiresEditClick &&
                                        parcelEditActive
                                    "
                                >
                                    <BaseButton
                                        type="button"
                                        icon="pi pi-times"
                                        rounded
                                        severity="secondary"
                                        class="!h-10 !w-10 shrink-0 border border-neutral-200 bg-white/95 shadow-sm"
                                        :aria-label="t('cancelParcelEdit')"
                                        v-tooltip.left="t('cancelParcelEdit')"
                                        data-cy="building-parcel-map-cancel-edit"
                                        @click="onCancelParcelEdit"
                                    />
                                </template>
                            </div>
                        </div>
                    </template>
                </BaseMap>
                <div
                    v-if="
                        showUndergroundBtHtaLines &&
                        getCenter &&
                        !parcelEditActive
                    "
                    class="mt-2 flex w-full max-w-full flex-wrap items-center justify-center gap-4"
                >
                    <button
                        type="button"
                        class="flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-xs text-surface-700 transition-colors hover:bg-surface-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        :class="
                            showUndergroundBtOnMap
                                ? ''
                                : 'opacity-60 saturate-50'
                        "
                        :aria-pressed="showUndergroundBtOnMap"
                        :aria-label="
                            showUndergroundBtOnMap
                                ? t('undergroundBtToggleHide')
                                : t('undergroundBtToggleShow')
                        "
                        v-tooltip.top="t('legendBtTooltip')"
                        data-cy="building-parcel-map-underground-lines-bt"
                        @click="onToggleUndergroundBtOnMap"
                    >
                        <span
                            class="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#e34a4a]"
                            aria-hidden="true"
                        />
                        {{ t("legendBt") }}
                    </button>
                    <button
                        type="button"
                        class="flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-xs text-surface-700 transition-colors hover:bg-surface-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        :class="
                            showUndergroundHtaOnMap
                                ? ''
                                : 'opacity-60 saturate-50'
                        "
                        :aria-pressed="showUndergroundHtaOnMap"
                        :aria-label="
                            showUndergroundHtaOnMap
                                ? t('undergroundHtaToggleHide')
                                : t('undergroundHtaToggleShow')
                        "
                        v-tooltip.top="t('legendHtaTooltip')"
                        data-cy="building-parcel-map-underground-lines-hta"
                        @click="onToggleUndergroundHtaOnMap"
                    >
                        <span
                            class="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-[#2563eb]"
                            aria-hidden="true"
                        />
                        {{ t("legendHta") }}
                    </button>
                </div>
                <div
                    v-if="showZoomHint && !readonly && parcelEditActive"
                    class="pointer-events-none absolute top-3 left-3 z-[1100] max-w-[min(100%,16rem)] rounded-md bg-white/95 px-3 py-2 text-xs text-primary shadow-sm border border-neutral-200"
                >
                    {{ t("zoomToSeeParcels") }}
                </div>
            </div>
            <div
                v-if="
                    !getLoading &&
                    !readonly &&
                    parcelEditActive &&
                    !disabledSkipButton &&
                    getCenter
                "
                class="building-parcel-map__skip flex justify-center items-center gap-2 mt-4"
            >
                <BaseCheckbox
                    :model-value="modelSkipParcel"
                    input-id="building-skip-parcel"
                    :binary="true"
                    :aria-label="t('skipLabel')"
                    data-cy="address-button-not-found"
                    @update:model-value="onUpdateSkipParcel"
                />
                <label
                    for="building-skip-parcel"
                    class="text-sm text-primary cursor-pointer select-none"
                >
                    {{ t("skipLabel") }}
                </label>
            </div>
        </template>
    </div>
</template>
